<template>
    <div style="max-height:835px;">
        <div id="prescription-image-component" v-if="(imageID || escriptID)" class="h-100">
            <div v-if="loadingImage" class="text-center m-4">
                <b-spinner small /> Loading Image...
            </div>

            <!-- Showing PDF -->
            <div ref="pdfContainer" v-if="!imageSrc" class="no-image h-100">
                <!-- Default message if PDF file was not found -->
                <h3 v-if="!loadingImage" class="m-4 text-center font-weight-bold text-muted">
                    <i class="fa fa-warning fa-sm" />
                    File not found
                </h3>
            </div>

            <!-- IMAGE FOUND -->
            <Viewer ref="viewer" v-else class="viewer" :options="viewerOptions()" :images="[imageSrc]">
                <template #default="scope">
                    <img v-for="src in scope.images" :src="src" :key="src" :alt="src" class="d-none">
                </template>
            </Viewer>
        </div>
        <!-- No image has been loaded. -->
        <div v-else-if="!rxNumber">
            <div class="no-image">
                <h4 class="m-4 text-center font-weight-bold text-muted">
                    <i class="fa fa-warning fa-sm" />
                    No RX Loaded
                </h4>
            </div>
        </div>
        <div v-else>
            <div class="no-image">
                <h4 class="m-4 text-center font-weight-bold text-muted">
                    <i class="fa fa-warning fa-sm" />
                    No image loaded in this prescription.
                </h4>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { EscriptResponse } from '@/models/Escript';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import PDFObject from 'pdfobject';
import { component as Viewer } from 'v-viewer';

@Component({
    name: "ImageComponent",
    components: {
        Viewer
    }
})
export default class ImageComponent extends Vue {
    @Prop() private imageID!: number;
    @Prop() private escriptID!: number;
    @Prop() protected rxNumber!: number;

    protected imageSrc?: any = null;

    private escriptDetailRequest?: any = null;
    private imageRequest?: any = null;
    private loadingImage = false;

    viewerOptions(): Viewer.Options {
        return {
            inline: true,
            button: false,
            title: false,
            transition: false,
            navbar: true,
            focus: false,
            toolbar: {
                zoomIn: 1,
                zoomOut: 1,
                reset: 1,
                prev: 1,
                next: 1,
                rotateLeft: 1,
                rotateRight: 1,
                flipHorizontal: 1,
                flipVertical: 1,
                oneToOne: 0,
                play: 0
            }
        };
    }

    created() {
        console.log("%c Created ImageComponent", "color: green;");
        if (this.imageID) {
            this.fetchPrescriptionImageSource(`image/${this.imageID}`);
        } else if (this.escriptID) {
            this.fetchPrescriptionEscriptResponse(this.escriptID);
        }
    }

    beforeDestroy() {
        console.log('%c Destroying ImageComponent', 'color: red');

        const viewer = this.$refs["viewer"] as any;
        if (viewer && viewer.destroy) {
            viewer.destroy();
        }

        const pdfContainer = this.$refs.pdfContainer as HTMLElement;
        if (pdfContainer) {
            pdfContainer.innerHTML = '';
        }
    }

    @Watch('imageID') imageIDUpdated(value: number) {
        if (value) {
            this.fetchPrescriptionImageSource(`image/${value}`);
        }
    }

    @Watch('escriptID') escriptIDUpdated(value: any) {
        if (value) {
            this.fetchPrescriptionEscriptResponse(value);
        }
    }

    async fetchPrescriptionEscriptResponse(value: number) {
        try {
            this.loadingImage = true;
            if (this.escriptDetailRequest) {
                this.escriptDetailRequest.cancel();
            }

            const axiosSource = Axios.CancelToken.source();

            this.escriptDetailRequest = { cancel: axiosSource.cancel };
            const response = await Axios.get<EscriptResponse>(`escript/${value}`, {
                cancelToken: axiosSource.token
            });

            const escript = response.data.eScript;
            await this.fetchPrescriptionImageSource(`escript/${escript.id}/image`);
        } finally {
            this.escriptDetailRequest = null;
            this.loadingImage = false;
        }
    }

    async fetchPrescriptionImageSource(imageUrl?: string) {
        if (!imageUrl) return;

        try {
            this.loadingImage = true;

            // Explicitly destroy viewer before switching to PDF
            const viewer = this.$refs["viewer"] as any;
            if (viewer && viewer.$viewer) {
                viewer.$viewer.destroy();
            }

            this.imageSrc = null;

            if (this.imageRequest) {
                this.imageRequest.cancel();
            }

            const axiosSource = Axios.CancelToken.source();

            this.imageRequest = { cancel: axiosSource.cancel };
            const urlResponse = await Axios.get(imageUrl, {
                cancelToken: axiosSource.token
            });

            const url = urlResponse.request.responseURL;
            const imageDataResponse = await Axios.get(url, {
                responseType: "blob",
                cancelToken: axiosSource.token
            })

            const objectUrl = URL.createObjectURL(imageDataResponse.data);

            const isPdf = urlResponse.headers["content-type"] === "application/pdf";

            if (!isPdf) {
                this.imageSrc = objectUrl;
                this.$nextTick(() => {
                    const viewer = this.$refs["viewer"] as any;
                    if (viewer) viewer.rebuildViewer();
                });
                return;
            }

            // Wait for Vue to destroy the Viewer component and render the pdfContainer
            await this.$nextTick();
            // Wait one more tick to ensure viewer DOM cleanup completes
            await this.$nextTick();

            const options = {
                pdfOpenParams: {
                    view: 'FitW',
                    pagemode: 'thumbs',
                    toolbar: 1,
                    scrollbar: 0,
                }
            };

            if (!this.$refs.pdfContainer) {
                throw "PDF container doesn't exist";
            }

            // Force remove this component's viewer container only (not other viewers on the page)
            const componentEl = this.$el as HTMLElement;
            if (componentEl) {
                const viewerContainers = componentEl.querySelectorAll('.viewer-container');
                viewerContainers.forEach(el => el.remove());
            }

            // Clear any existing content before embedding new PDF
            (this.$refs.pdfContainer as HTMLElement).innerHTML = '';

            PDFObject.embed(objectUrl, this.$refs.pdfContainer, options);

            this.$nextTick(() => {
                this.$forceUpdate();
            });
        } catch (err) {
            console.error(err)
        }
        finally {
            this.imageRequest = null;
            this.loadingImage = false;
        }
    }
}
</script>

<style scoped>
#prescription-image-component {
    resize: both;
    z-index: 100;
}
</style>
